import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef, useState } from 'react';
import { Polaroid } from '../components/ui/Polaroid';
import { RevealText, InkUnderline } from '../components/ui/RevealText';
import { MarketingNav, BackToTop } from '../components/ui/MarketingNav';

export default function HomePage() {
  return (
    <div className="min-h-screen">
      <MarketingNav />

      <Hero />
      <Marquee />
      <DemoLive />
      <Manifesto />
      <Features />
      <HowItWorks />
      <Voices />
      <Pricing />
      <FAQ />
      <Footer />
      <BackToTop />
    </div>
  );
}

/* ───────────────────── Hero ───────────────────── */

function Hero() {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  // Subtle parallax: the photo cluster drifts up while the headline drifts up slower
  const clusterY = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const headlineY = useTransform(scrollYProgress, [0, 1], [0, -20]);

  return (
    <section ref={ref} className="container-page relative pt-md pb-3xl">
      {/* date / issue marker — top-right like a magazine masthead */}
      <div className="mb-xl flex items-center justify-between">
        <span className="kicker">Ediția I · Septembrie 2026</span>
        <span className="hidden font-mono text-xs uppercase tracking-[0.2em] text-fg-soft md:inline">
          Pentru cei care păstrează tot
        </span>
      </div>

      <div className="grid grid-cols-1 gap-xl lg:grid-cols-12">
        <motion.div style={{ y: headlineY }} className="lg:col-span-7">
          <RevealText
            as="h1"
            className="leading-[0.92]"
            delay={0.2}
            stagger={0.05}
          >
            {`Ochii invitaților tăi, `}
          </RevealText>
          <RevealText
            as="h1"
            className="leading-[0.92]"
            delay={0.55}
            stagger={0.05}
          >
            {`lipiți într-o revistă. `}
          </RevealText>
          <h1
            className="font-serif text-[clamp(3.5rem,7vw,6.5rem)] leading-[0.92] tracking-tight"
            style={{ fontVariationSettings: "'opsz' 144, 'SOFT' 100, 'WONK' 1" }}
          >
            <span className="text-gradient italic">Nu într-un folder.</span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.2 }}
            className="mt-xl max-w-md text-lg text-fg-soft"
          >
            Un cod QR pe masă, telefoanele invitaților,{' '}
            <InkUnderline>zero aplicații</InkUnderline>. A doua zi dimineața
            te trezești cu o revistă plină cu ziua ta, văzută prin{' '}
            ochii tuturor.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.4 }}
            className="mt-xl flex flex-wrap items-center gap-sm"
          >
            <a href="#demo-live" className="btn-primary">
              Deschide galeria demo
            </a>
            <a href="#cum-functioneaza" className="btn-ghost">
              Cum funcționează →
            </a>
          </motion.div>
        </motion.div>

        <motion.div
          style={{ y: clusterY }}
          className="relative lg:col-span-5"
        >
          {/* Main photo, slightly tilted, taped at top-left and bottom-right (diagonal) */}
          <div className="relative">
            <Polaroid
              src="https://images.unsplash.com/photo-1519741497674-611481863552?w=900&q=80"
              alt="Eli și Marcu, ziua nunții lor"
              caption="Eli & Marcu · 29 august"
              rotate={-3}
              aspect="4/5"
              delay={300}
            />
          </div>
          {/* Smaller secondary photo overlapping bottom-left */}
          <div className="-mt-12 ml-6 hidden w-3/5 sm:block">
            <Polaroid
              src="https://images.unsplash.com/photo-1606800052052-a08af7148866?w=600&q=80"
              alt="Petrecerea, văzută de pe ringul de dans"
              caption="Ringul de dans, 23:14"
              rotate={4}
              aspect="1/1"
              delay={550}
            />
          </div>
          {/* Tiny tertiary photo top-right */}
          <div className="absolute right-2 -top-4 hidden w-2/5 rotate-2 sm:block">
            <Polaroid
              src="https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=400&q=80"
              alt="Un moment din zi"
              caption="✦ 11 poze / minut"
              rotate={6}
              aspect="1/1"
              delay={750}
            />
          </div>
        </motion.div>
      </div>

      {/* hairline + dată */}
      <div className="mt-2xl">
        <div className="hairline">
          <span>Citește mai departe</span>
        </div>
      </div>
    </section>
  );
}

/* ───────────────────── Marquee (dată strip) ───────────────────── */

function Marquee() {
  // duplicate content so the loop is seamless
  const items = [
    '4 demo-uri live',
    '3 moduri de vizualizare',
    'Moderare ușoară',
    'Stocare 1 an inclusă',
    'Fără aplicație, fără cont',
    'QR-uri printabile per masă',
  ];
  const doubled = [...items, ...items];

  return (
    <section
      aria-hidden
      className="overflow-hidden border-y border-border bg-bg-alt py-md"
    >
      <div className="editorial-ticker">
        {doubled.map((t, i) => (
          <span key={i}>{t}</span>
        ))}
      </div>
    </section>
  );
}

/* ───────────────────── Demo Live ───────────────────── */

const DEMO_EVENTS = [
  {
    slug: 'nunta-eli-si-marcu',
    label: 'Eli & Marcu',
    tag: 'Nuntă',
    cover: 'https://images.unsplash.com/photo-1519741497674-611481863552?w=700&q=80',
    blurb: '218 invitați · 1.842 de cadre · 3 zile de petrecere.',
    rotate: -2.5,
  },
  {
    slug: 'botezul-lui-teo',
    label: 'Botezul lui Teo',
    tag: 'Botez',
    cover: 'https://images.unsplash.com/photo-1532712938310-34cb3982ef74?w=700&q=80',
    blurb: 'Familie mică, galerie mare. 47 de oameni, 612 cadre.',
    rotate: 1.8,
  },
  {
    slug: 'lansare-firma-acme',
    label: 'Christmas Party',
    tag: 'Corporate',
    cover: 'https://images.unsplash.com/photo-1556742502-ec7c0e9f34b1?w=700&q=80',
    blurb: 'Brand launch, 90 de invitați, brief în 24h.',
    rotate: 2.2,
  },
  {
    slug: 'majorat-ana',
    label: 'Majoratul Annei',
    tag: 'Majorat',
    cover: 'https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=700&q=80',
    blurb: '18 ani, 130 de prieteni, o noapte de neratat.',
    rotate: -1.5,
  },
];

function DemoLive() {
  return (
    <section id="demo-live" className="container-page py-3xl">
      <div className="mb-2xl flex flex-col items-baseline justify-between gap-md sm:flex-row">
        <div>
          <p className="kicker">Demo live</p>
          <h2
            className="mt-sm max-w-xl"
            style={{ fontVariationSettings: "'opsz' 144" }}
          >
            Patru feluri de a trăi o zi.
            <br />
            <span className="italic text-fg-soft">Același album, la final.</span>
          </h2>
        </div>
        <p className="max-w-sm text-fg-soft">
          Alege un demo ca să vezi cum arată galeria pentru tipul tău de
          eveniment. Fiecare link e public, n-are nevoie de cont.
        </p>
      </div>

      {/* Disclaimer: pozele din demo-uri sunt demonstrative (Unsplash), nu de la evenimente reale */}
      <p className="mb-lg font-mono text-[0.7rem] uppercase tracking-[0.18em] text-fg-soft/70">
        ✦ Pozele de mai jos sunt demonstrative (Unsplash), nu din evenimente reale.
      </p>

      {/* Masonry-ish: 4 polaroids cu înălțimi diferite prin aspect diferit */}
      <div className="grid grid-cols-1 gap-lg sm:grid-cols-2 lg:grid-cols-4">
        {DEMO_EVENTS.map((e, i) => (
          <motion.div
            key={e.slug}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ delay: i * 0.1, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-col"
          >
            <Link to={`/e/${e.slug}`} className="group block">
              <Polaroid
                src={e.cover}
                alt={`Galeria demo pentru ${e.tag}`}
                tag={e.tag}
                rotate={e.rotate}
                aspect={i % 2 === 0 ? '4/5' : '3/4'}
                delay={i * 80}
              />
              <div className="mt-md px-2">
                <h3 className="font-serif text-xl">{e.label}</h3>
                <p className="mt-xs text-sm text-fg-soft">{e.blurb}</p>
                <span className="mt-sm inline-block font-mono text-xs uppercase tracking-[0.2em] text-accent">
                  Deschide galeria →
                </span>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

/* ───────────────────── Manifesto ───────────────────── */

function Manifesto() {
  return (
    <section id="manifesto" className="bg-bg-alt py-4xl">
      <div className="container-page paper-grain relative">
        <div className="mx-auto max-w-3xl text-center">
          <p className="kicker justify-center">Misiunea noastră</p>

          <h2
            className="mt-md font-serif leading-[1.05]"
            style={{
              fontSize: 'clamp(2rem, 4vw, 3.4rem)',
              fontVariationSettings: "'opsz' 144",
            }}
          >
            O poză nu e un fișier. E{' '}
            <em className="text-gradient">o amintire</em> care merită să fie{' '}
            <InkUnderline>ținută în mână</InkUnderline>.
          </h2>

          <div className="squiggle mx-auto mt-lg" />

          <p className="mt-lg text-lg text-fg-soft">
            Ne-am săturat să vedem nunți frumoase pierdute în foldere ZIP,
            botezuri uitate în iCloud și majorate comprimate în conversații
            de WhatsApp. Am construit GuestLens pentru că pozele unui
            eveniment merită mai mult decât un link într-un chat.
          </p>
          <p className="mt-md text-lg text-fg-soft">
            Merită o revistă. Una pe care o poți arăta nepoților, o poți
            pune pe masă la 10 ani de la nuntă, o poți răsfoi la
            cafea.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ───────────────────── Features ───────────────────── */

const FEATURES = [
  {
    title: 'Zero fricțiune.',
    sub: 'Invitatul n-are nevoie de nimic.',
    desc: 'Nu instalezi aplicație, nu-ți faci cont, nu-ți dai mailul. Scanezi un cod cu camera, apeși „Încarcă" și gata. Funcționează pe orice telefon făcut după 2016.',
    img: 'https://images.unsplash.com/photo-1556742502-ec7c0e9f34b1?w=900&q=80',
    rotate: 2,
    tag: 'Fără cont',
  },
  {
    title: 'Moderare blândă.',
    sub: 'Tu alegi ce rămâne, nu AI-ul.',
    desc: 'Toate pozele ajung într-o coadă privată. Glisezi cu degetul pe cele care merită, celelalte rămân în seiful tău digital. Fără fețe neclare la ora 3 dimineața, fără poze cu ochii închiși trimise la toată lista.',
    img: 'https://images.unsplash.com/photo-1606800052052-a08af7148866?w=900&q=80',
    rotate: -1.5,
    tag: 'Tu decizi',
  },
  {
    title: 'Ca o revistă.',
    sub: 'Trei moduri de citit galeria.',
    desc: 'Modul Revistă e pentru o seară liniștită, cu swipe-uri lungi și tipografie mare. Modul Grilă e pentru „vreau să văd totul acum". Modul Noutăți e pentru diminețile cu cafea — doar ce-a apărut în ultimele 24h.',
    img: 'https://images.unsplash.com/photo-1469371670807-013ccf25f16a?w=900&q=80',
    rotate: 1.2,
    tag: '3 moduri',
  },
];

function Features() {
  return (
    <section className="container-page py-4xl">
      <div className="mb-2xl flex items-end justify-between">
        <div>
          <p className="kicker">Ce face</p>
          <h2 className="mt-sm max-w-md" style={{ fontVariationSettings: "'opsz' 144" }}>
            Nu e Dropbox.
            <br />
            <em className="text-fg-soft italic">E altceva.</em>
          </h2>
        </div>
        <span className="hidden font-mono text-xs uppercase tracking-[0.2em] text-fg-soft md:inline">
          — 01 / 02 / 03
        </span>
      </div>

      <div className="space-y-4xl">
        {FEATURES.map((f, i) => (
          <motion.div
            key={f.title}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.8 }}
            className={`grid grid-cols-1 items-center gap-xl lg:grid-cols-12`}
          >
            <div className={`lg:col-span-5 ${i % 2 === 1 ? 'lg:col-start-8' : ''}`}>
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-fg-soft">
                — 0{i + 1}
              </p>
              <h3 className="mt-sm font-serif" style={{ fontVariationSettings: "'opsz' 144" }}>
                {f.title}
              </h3>
              <p className="mt-sm font-serif text-lg italic text-fg">
                {f.sub}
              </p>
              <p className="mt-md text-fg-soft">{f.desc}</p>
            </div>

            <div
              className={`lg:col-span-6 ${
                i % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''
              }`}
            >
              <Polaroid
                src={f.img}
                alt={f.title}
                caption={f.tag}
                tag={f.tag}
                rotate={f.rotate}
                aspect="4/3"
                delay={i * 120}
              />
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

/* ───────────────────── How It Works ───────────────────── */

const STEPS = [
  {
    n: '01',
    when: 'cu 2 săptămâni înainte',
    title: 'Tipărești QR-ul.',
    body:
      'Un singur PDF cu 12 coduri, câte unul per masă. Le pui în plicuri, pe fețe de masă, lângă aranjament, unde vrei. Arată ca niște cartonașe de meniu, nu ca o reclamă la SaaS.',
  },
  {
    n: '02',
    when: 'în ziua evenimentului',
    title: 'Invitații scanează, încarcă, uită de telefon.',
    body:
      'Fiecare face poze de pe telefonul lui, fără cont, fără aplicație. Când pun telefonul jos, pozele sunt deja la tine — sortate pe masă, pe oră, pe moment. Nu trebuie să alergi după nimeni cu un cablu.',
  },
  {
    n: '03',
    when: 'a doua zi',
    title: 'Aprobi cu o antigere.',
    body:
      'Deschizi telefonul, glisezi prin miniaturi, apeși o dată pe cele care merită. Restul rămân private, în seiful tău. Peste 30 de zile, cât durează majoritatea nunților să se „așeze", primești un PDF gata de printat, format revistă, copertă inclusă.',
  },
];

function HowItWorks() {
  return (
    <section id="cum-functioneaza" className="container-page py-4xl">
      <div className="grid grid-cols-1 gap-xl lg:grid-cols-12">
        <div className="lg:col-span-5">
          <p className="kicker">Cum funcționează</p>
          <h2 className="mt-sm" style={{ fontVariationSettings: "'opsz' 144" }}>
            Trei pași.
            <br />
            <em className="text-fg-soft italic">Și s-a făcut.</em>
          </h2>
          <p className="mt-md max-w-sm text-fg-soft">
            Nimic de instalat, nimic de configurat, niciun tutorial de 7
            minute. Doar un cod pe masă și telefoanele invitaților — pe
            care oricum le au deja în mână.
          </p>

          <div className="mt-xl">
            <Polaroid
              src="https://images.unsplash.com/photo-1583939003579-730e3918a45a?w=700&q=80"
              alt="Un telefon cu un cod QR scanat"
              caption="✦ un singur tap"
              rotate={-2}
              aspect="4/5"
            />
          </div>

          <div className="mt-lg">
            <Link to="/cum-functioneaza" className="btn-secondary">
              Vezi tot despre cum funcționează →
            </Link>
            <p className="mt-xs font-mono text-[0.7rem] uppercase tracking-[0.18em] text-fg-soft">
              Pagina detaliată: cronologie, roluri, ce nu facem
            </p>
          </div>
        </div>

        <div className="space-y-2xl lg:col-span-7">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ delay: i * 0.12, duration: 0.7 }}
              className="border-l-2 border-accent pl-lg"
            >
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-fg-soft">
                {s.n} — {s.when}
              </p>
              <h3 className="mt-sm font-serif text-2xl" style={{ fontVariationSettings: "'opsz' 144" }}>
                {s.title}
              </h3>
              <p className="mt-sm max-w-xl text-fg-soft">{s.body}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ───────────────────── Voices (testimoniale reale-style) ───────────────────── */

const VOICES = [
  {
    quote:
      'Am crezut că o să fie încă un link pe care să nu-l mai deschidă nimeni. A doua zi dimineața aveam 1.842 de cadre și o ceartă în familie pe care le publicăm primele.',
    who: 'Mireasa, nuntă cu 180 de invitați',
    when: 'iulie 2026',
  },
  {
    quote:
      'Noi n-am vrut fotograf. Am vrut doar ce văd prietenii noștri. Exact asta am primit — o revistă cu 600 de cadre pe care niciun fotograf profesionist nu le-ar fi prins.',
    who: 'Nașii, botez în Brașov',
    when: 'octombrie 2025',
  },
  {
    quote:
      'Am printat-o pe hârtie. E pe peretele din bucătărie. N-am făcut niciodată asta cu un album de Facebook.',
    who: 'Mama miresei',
    when: 'august 2026',
  },
];

function Voices() {
  return (
    <section className="bg-bg-alt py-4xl">
      <div className="container-page">
        <div className="mb-2xl mx-auto max-w-2xl text-center">
          <p className="kicker justify-center">Voci</p>
          <h2 className="mt-sm" style={{ fontVariationSettings: "'opsz' 144" }}>
            Ce zic oamenii când se uită la galeria lor.
          </h2>
        </div>

        <div className="grid grid-cols-1 gap-lg md:grid-cols-3">
          {VOICES.map((v, i) => (
            <motion.figure
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ delay: i * 0.1, duration: 0.7 }}
              className="flex flex-col"
            >
              <blockquote className="pull-quote">
                {v.quote}
              </blockquote>
              <figcaption className="mt-md flex items-center gap-md border-t border-border pt-md font-mono text-xs uppercase tracking-[0.18em] text-fg-soft">
                <span className="h-1.5 w-1.5 rounded-full bg-accent" />
                {v.who} · {v.when}
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ───────────────────── Pricing ───────────────────── */

function Pricing() {
  return (
    <section id="preturi" className="container-page py-4xl">
      <div className="grid grid-cols-1 items-center gap-2xl lg:grid-cols-12">
        <div className="lg:col-span-5">
          <p className="kicker">Prețuri</p>
          <h2 className="mt-sm" style={{ fontVariationSettings: "'opsz' 144" }}>
            Simplu,
            <br />
            <em className="text-fg-soft italic">ca tot ce facem.</em>
          </h2>
          <p className="mt-md text-fg-soft">
            Plătești per eveniment, nu per invitație. Fără abonament, fără
            surprize la final de lună, fără costuri ascunse pentru „stocare
            premium". 749 de lei e tot. Și include un PDF de revistă
            printabil, gata de dus la tipografie.
          </p>
          <p className="mt-sm text-fg-soft">
            Pentru evenimente mai mari sau galerii corporate cu brand
            guidelines — <a href="mailto:guestlens.events@gmail.com" className="text-accent hover:underline">scrie-ne</a>.
          </p>
        </div>

        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 30, rotate: -2 }}
            whileInView={{ opacity: 1, y: 0, rotate: -0.5 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <Polaroid
              src="https://images.unsplash.com/photo-1606800052052-a08af7148866?w=900&q=80"
              alt="Imagine editorială pentru prețuri"
              caption="✦ un singur plan, tot inclus"
              rotate={0.5}
              aspect="3/2"
            />
            {/* price sticker overlapping */}
            <div
              className="absolute -left-6 -top-6 z-10 hidden rotate-[-6deg] rounded-md bg-bg p-md shadow-lg md:block"
              style={{ boxShadow: 'var(--shadow-lg)' }}
            >
              <p className="font-mono text-[0.65rem] uppercase tracking-[0.22em] text-fg-soft">
                Un singur plan
              </p>
              <p
                className="mt-xs font-serif leading-none text-fg"
                style={{ fontSize: '4.4rem', fontVariationSettings: "'opsz' 144" }}
              >
                749
              </p>
              <p className="mt-xs text-sm text-fg-soft">lei / eveniment</p>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="mt-2xl grid grid-cols-1 gap-md sm:grid-cols-2 lg:grid-cols-3">
        {[
          ['Poze nelimitate', 'De la toți invitații, fără plafon.'],
          ['3 moduri de vizualizare', 'Revistă, Grilă, Noutăți.'],
          ['Moderare blândă', 'Tu decizi, tu semnezi.'],
          ['Comentarii & like-uri', 'Reacții de la invitați, pe loc.'],
          ['Stocare inclusă 1 an', 'Apoi 49 lei/an, cât vrei.'],
          ['Link personalizat', 'ușor de trimis și de reținut.'],
        ].map(([title, body]) => (
          <div key={title} className="border-t border-border pt-md">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-accent">
              ✓
            </p>
            <p className="mt-sm font-serif text-lg">{title}</p>
            <p className="mt-xs text-sm text-fg-soft">{body}</p>
          </div>
        ))}
      </div>

      <div className="mt-2xl flex flex-col items-center gap-sm">
        <Link to="/contact" className="btn-primary">
          Începe acum
        </Link>
        <Link to="/preturi" className="btn-ghost">
          Vezi pagina completă de prețuri →
        </Link>
        <p className="mt-sm font-mono text-xs uppercase tracking-[0.2em] text-fg-soft">
          Plătești doar când e gata evenimentul
        </p>
      </div>
    </section>
  );
}

/* ───────────────────── FAQ ───────────────────── */

const FAQS = [
  {
    q: 'Dar confidențialitatea?',
    a: 'Galeria publică arată doar ce-ai aprobat tu. Restul rămâne în seiful tău digital, vizibil doar cu contul tău. Invitații văd doar pozele publice și pe ale lor — nu pe ale altora. La final, îți poți șterge tot oricând, fără să ne scrii un email.',
  },
  {
    q: 'Ce se întâmplă dacă un invitat încarcă ceva nepotrivit?',
    a: 'Nu apare public. Ajunge direct în coada ta de moderare, cu un semn discret că e „neverificat". Dacă e ceva cu adevărat problematic, ne scrii și-l ștergem în 24h.',
  },
  {
    q: 'Funcționează fără internet la sală?',
    a: 'Invitații pot face poze, dar upload-ul așteaptă semnal. Noi recomandăm să pui un mic hotspot sau să te asiguri că sala are WiFi. Alternativ, încărcarea se face automat când telefonul revine la semnal — nimeni nu pierde nimic.',
  },
  {
    q: 'Pot să-mi fac singur design-ul?',
    a: 'Momentan nu — galeria vine cu cele trei moduri (Revistă, Grilă, Noutăți) și atât. E destul pentru 95% din ce am văzut la nunți și botezuri. Dacă ai nevoie de custom pentru un eveniment corporate, scrie-ne.',
  },
  {
    q: 'Cât costă dacă am 500 de invitați?',
    a: 'Tot 749 de lei. Plătești per eveniment, nu per persoană. Invită 50, invită 500, invită 1500 — același preț.',
  },
  {
    q: 'Când primesc pozele?',
    a: 'Imediat ce le încarcă invitații, le vezi în coadă. Galeria publică se deschide când apeși „Publică". PDF-ul de revistă e gata în 24h după ce ai moderat tot.',
  },
];

function FAQ() {
  const [open, setOpen] = useState<number | null>(0);

  return (
    <section id="intrebari" className="container-page py-4xl">
      <div className="mx-auto max-w-3xl">
        <div className="text-center">
          <p className="kicker justify-center">Întrebări pe care le-am primit</p>
          <h2 className="mt-sm" style={{ fontVariationSettings: "'opsz' 144" }}>
            Ne-am gândit la ele <em className="text-fg-soft italic">înainte să le pui</em>.
          </h2>
        </div>

        <div className="mt-2xl divide-y divide-border">
          {FAQS.map((f, i) => (
            <motion.div
              key={f.q}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
            >
              <button
                onClick={() => setOpen(open === i ? null : i)}
                className="block w-full py-md text-left"
              >
                <div className="flex items-baseline justify-between gap-md">
                  <p className="font-serif text-lg">
                    <span className="mr-md font-mono text-sm text-fg-soft">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    {f.q}
                  </p>
                  <span className="font-mono text-lg text-fg-soft">
                    {open === i ? '−' : '+'}
                  </span>
                </div>
                {open === i && (
                  <p className="mt-sm pl-lg text-fg-soft">{f.a}</p>
                )}
              </button>
            </motion.div>
          ))}
        </div>

        <p className="mt-2xl text-center text-fg-soft">
          Altceva?{' '}
          <a href="mailto:guestlens.events@gmail.com" className="text-accent hover:underline">
            Scrie-ne direct
          </a>
          , răspundem în aceeași zi.
        </p>
      </div>
    </section>
  );
}

/* ───────────────────── Footer ───────────────────── */

function Footer() {
  return (
    <footer className="border-t border-border py-3xl">
      <div className="container-page">
        <div className="grid grid-cols-1 gap-2xl sm:grid-cols-4">
          <div className="sm:col-span-2">
            <p
              className="font-serif"
              style={{ fontSize: '2.4rem', lineHeight: 1, fontVariationSettings: "'opsz' 144" }}
            >
              GuestLens
            </p>
            <p className="mt-md max-w-sm text-fg-soft">
              Făcut cu răbdare pentru nunți, botezuri și oameni care vor
              să păstreze tot — nu ca fișiere, ci ca amintiri pe care le
              poți răsfoi.
            </p>
          </div>
          <div>
            <p className="kicker">Produs</p>
            <ul className="mt-sm space-y-xs text-sm text-fg-soft">
              <li><Link to="/cum-functioneaza" className="hover:text-fg">Cum funcționează</Link></li>
              <li><Link to="/preturi" className="hover:text-fg">Prețuri</Link></li>
              <li><Link to="/despre" className="hover:text-fg">Despre</Link></li>
              <li><Link to="/misiune" className="hover:text-fg">Misiune</Link></li>
            </ul>
          </div>
          <div>
            <p className="kicker">Contact</p>
            <ul className="mt-sm space-y-xs text-sm text-fg-soft">
              <li><a href="mailto:guestlens.events@gmail.com" className="hover:text-fg">guestlens.events@gmail.com</a></li>
              <li>In toate judetele disponibile</li>
            </ul>
          </div>
        </div>

        <div className="mt-2xl flex flex-col items-center justify-between gap-md border-t border-border pt-md sm:flex-row">
          <p className="text-xs text-fg-soft">
            © 2026 GuestLens · Toate drepturile rezervate
          </p>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-fg-soft">
            Tipărit digital · Ediția I
          </p>
        </div>
      </div>
    </footer>
  );
}
